/**
 * HelpSlideOut — Wraps Help Centre in a slide-out panel
 *
 * Displays the full Help Centre interface (search + article browsing)
 * inside a slide-out panel instead of a full page view.
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, X, ArrowLeft, BookOpen, Maximize2 } from 'lucide-react';
import SlideOutPanel from '../common/SlideOutPanel';
import Card from '../common/Card';
import { cn } from '../../lib/utils';

// Import help article system from HelpPage
import { ARTICLES, SECTIONS, SECTION_ICONS } from '../../pages/HelpPage';

interface ArticleContentProps {
  article: typeof ARTICLES[0];
  query: string;
}

/**
 * Extract searchable text from an article
 */
function extractText(article: typeof ARTICLES[0]): string {
  const parts: string[] = [
    article.section,
    article.title,
    ...article.keywords,
    ...article.blocks
      .map((block) => {
        if (block.type === 'p' || block.type === 'h3' || block.type === 'note')
          return block.text || '';
        if (block.type === 'ul') return (block.items || []).join(' ');
        if (block.type === 'table')
          return [
            ...(block.headers || []),
            ...(block.rows || []).flat(),
          ].join(' ');
        return '';
      })
      .filter(Boolean),
  ];
  return parts.join(' ').toLowerCase();
}

/**
 * Search articles
 */
function searchArticles(query: string): typeof ARTICLES {
  if (!query.trim()) return [];

  const searchTerm = query.toLowerCase();
  const corpus = ARTICLES.map((a) => ({
    article: a,
    corpus: extractText(a),
  }));

  return corpus
    .filter((item) => item.corpus.includes(searchTerm))
    .map((item) => item.article);
}

/**
 * Highlight search terms in text
 */
function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;

  const regex = new RegExp(`(${query})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-200 dark:bg-yellow-900 font-semibold">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
}

/**
 * Article content renderer
 */
function ArticleContent({ article, query }: ArticleContentProps) {
  return (
    <div className="space-y-4 text-sm">
      {article.blocks.map((block, i) => {
        if (block.type === 'p')
          return (
            <p key={i} className="text-gray-700 dark:text-gray-300">
              {highlightText(block.text || '', query)}
            </p>
          );

        if (block.type === 'h3')
          return (
            <h3
              key={i}
              className="font-semibold text-gray-900 dark:text-white mt-4 mb-2"
            >
              {highlightText(block.text || '', query)}
            </h3>
          );

        if (block.type === 'ul')
          return (
            <ul key={i} className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
              {(block.items || []).map((item, j) => (
                <li key={j}>{highlightText(item, query)}</li>
              ))}
            </ul>
          );

        if (block.type === 'note')
          return (
            <div
              key={i}
              className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-blue-900 dark:text-blue-200 text-sm"
            >
              {highlightText(block.text || '', query)}
            </div>
          );

        if (block.type === 'table')
          return (
            <table
              key={i}
              className="w-full text-sm border-collapse border border-gray-300 dark:border-gray-700"
            >
              <thead className="bg-gray-100 dark:bg-gray-800">
                <tr>
                  {(block.headers || []).map((h, j) => (
                    <th
                      key={j}
                      className="border border-gray-300 dark:border-gray-700 p-2 text-left font-semibold"
                    >
                      {highlightText(h, query)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(block.rows || []).map((row, j) => (
                  <tr key={j}>
                    {row.map((cell, k) => (
                      <td
                        key={k}
                        className="border border-gray-300 dark:border-gray-700 p-2"
                      >
                        {highlightText(cell, query)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );

        return null;
      })}
    </div>
  );
}

/**
 * Search result item
 */
function SearchResult({
  article,
  query,
  onSelect,
}: {
  article: typeof ARTICLES[0];
  query: string;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(article.id)}
      className="w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-metallic-800 transition-colors border border-gray-200 dark:border-metallic-700"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {article.section}
          </p>
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
            {highlightText(article.title, query)}
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
            {String(highlightText(extractText(article), query)).substring(0, 100)}...
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
      </div>
    </button>
  );
}

/**
 * HelpSlideOut component
 */
export interface HelpSlideOutProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HelpSlideOut({ isOpen, onClose }: HelpSlideOutProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeArticleId, setActiveArticleId] = useState<string | null>(null);

  const results = useMemo(() => searchArticles(query), [query]);
  const activeArticle = useMemo(
    () => ARTICLES.find((a) => a.id === activeArticleId) ?? null,
    [activeArticleId]
  );

  const handleSelectArticle = useCallback((id: string) => {
    setActiveArticleId(id);
    setQuery('');
  }, []);

  const handleBack = useCallback(() => {
    setActiveArticleId(null);
  }, []);

  // Maximize button — lives in the panel title bar, always visible on scroll
  const maximizeBtn = (
    <button
      onClick={() => { onClose(); navigate('/help'); }}
      className="p-1.5 rounded-lg transition-colors text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-metallic-800"
      aria-label="Open full Help page"
      title="Open full page"
    >
      <Maximize2 className="w-5 h-5" />
    </button>
  );

  // Article detail view
  if (activeArticle) {
    return (
      <SlideOutPanel
        isOpen={isOpen}
        onClose={onClose}
        title={activeArticle.title}
        width="wide"
        headerActions={maximizeBtn}
      >
        <div className="space-y-4">
          {/* Back button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to articles
          </button>

          {/* Section badge */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {activeArticle.section}
            </p>
          </div>

          {/* Article content */}
          <ArticleContent article={activeArticle} query={query} />
        </div>
      </SlideOutPanel>
    );
  }

  // Main help centre view
  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Help Centre"
      width="medium"
      headerActions={maximizeBtn}
    >
      <div className="space-y-4">
        {/* Search bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search help articles..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-metallic-700 rounded-lg bg-white dark:bg-metallic-800 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoFocus
          />
        </div>

        {/* Search results */}
        {query.trim() && (
          <div className="space-y-2">
            {results.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No articles found for <strong>"{query}"</strong>
              </p>
            ) : (
              <>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {results.length} article{results.length !== 1 ? 's' : ''} found
                </p>
                <div className="space-y-2">
                  {results.map((article) => (
                    <SearchResult
                      key={article.id}
                      article={article}
                      query={query}
                      onSelect={handleSelectArticle}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Sections grid */}
        {!query.trim() && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Browse sections
            </p>
            {SECTIONS.map((section) => {
              const Icon = SECTION_ICONS[section] ?? BookOpen;
              const articles = ARTICLES.filter((a) => a.section === section);
              return (
                <div key={section} className="border border-gray-200 dark:border-metallic-700 rounded-lg overflow-hidden">
                  {/* Section header */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-metallic-800 border-b border-gray-200 dark:border-metallic-700">
                    <Icon className="w-4 h-4 text-primary-600 flex-shrink-0" />
                    <h3 className="text-xs font-semibold text-gray-800 dark:text-gray-200 flex-1">
                      {section}
                    </h3>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {articles.length}
                    </span>
                  </div>
                  {/* Article list */}
                  <ul className="divide-y divide-gray-100 dark:divide-metallic-800/50">
                    {articles.slice(0, 3).map((article) => (
                      <li key={article.id}>
                        <button
                          onClick={() => handleSelectArticle(article.id)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-metallic-700/50 transition-colors text-sm text-gray-700 dark:text-gray-300"
                        >
                          {article.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {articles.length > 3 && (
                    <button className="w-full text-left px-3 py-2 text-xs text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-metallic-700/50 transition-colors font-medium">
                      +{articles.length - 3} more
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SlideOutPanel>
  );
}

export default HelpSlideOut;

// Made with Bob
